// src/components/CustomerMessagesModal.tsx
import React from 'react';
import { Table, Space, Button, Empty, Typography, Popconfirm, Input } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import type { QuestionMessage } from '../types/QuestionMessage';
import { useResizableColumns, ResCol } from '../hooks/useResizableColumns';
import { formatAmPhoneDisplay, digitsOnly } from '../utils/phone';
import NotifModalShell from './NotifModalShell';
import { useModalTableState } from '../hooks/useModalTableState';
import { formatDateTime } from '../utils/dates';

type Props = {
  open: boolean;
  onClose: () => void;
  questions: QuestionMessage[];
  onMarkRead: (ids: string[]) => void;
  onDelete: (ids: string[]) => void;
  total?: number;
  page?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
};

const CustomerMessagesModal: React.FC<Props> = ({
  open, onClose, questions, onMarkRead, onDelete,
  total, page, pageSize, onPageChange
}) => {
       // ◀ поиск
const {
  data,
  selected, setSelected,
  search, setSearch,
  anySelected, anyUnreadSelected,
} = useModalTableState<QuestionMessage>(questions, {
  unreadPredicate: (m) => m.isRead === false,
  filterFn: (m, q) => {
    const name = (m.name ?? '').toLowerCase();
    const phoneDigits = digitsOnly(m.phone ?? '');
    const qDigits = digitsOnly(q);
    return name.includes(q) || (!!qDigits && phoneDigits.includes(qDigits));
  },
  debounceMs: 150,
});

  // базовые колонки
  const baseColumns: ResCol<QuestionMessage>[] = [
    { key: 'name',     title: 'Имя',       dataIndex: 'name',      width: 160 },
    { key: 'phone',    title: 'Телефон',   dataIndex: 'phone',     width: 150,
      render: (v?: string) => formatAmPhoneDisplay(v)
    },
    { key: 'message',  title: 'Сообщение', dataIndex: 'message',   width: 380,
      render: (v: string) => <div style={{ whiteSpace: 'pre-wrap' }}>{v}</div>
    },
    { key: 'createdAt', title: 'Дата', dataIndex: 'createdAt', width: 180,
      render: (v: string) => formatDateTime(v)
    },
    {
      key: 'actions',
      title: '',
      dataIndex: 'actions',
      width: 210,
      align: 'right',
      resizable: false,
      render: (_: any, r: QuestionMessage) => (
        <Space>
          {!r.isRead && (
            <Button size="small" type="primary" ghost onClick={() => onMarkRead([r.id])}>
              Прочитано
            </Button>
          )}
          <Popconfirm
            title="Удалить сообщение?"
            okText="Да"
            cancelText="Отмена"
            onConfirm={() => onDelete([r.id])}
          >
            <Button size="small" danger>Удалить</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const { columns, components } = useResizableColumns<QuestionMessage>(baseColumns, {
    storageKey: 'table.customerMessages.cols.v1',
    enableVisibility: true,
    enableReorder: true,
  });


return (
  <NotifModalShell
    title="Сообщения от покупателей"
    open={open}
    onClose={onClose}
    width={900}
    leftControls={
      <>
        <Button
          disabled={!anyUnreadSelected}
          type="primary"
          ghost
          onClick={() => {
            const ids = data.filter(d => selected.includes(d.id) && !d.isRead).map(d => d.id);
            onMarkRead(ids);
            setSelected([]);
          }}
        >
          Прочитано (выбранные)
        </Button>

        <Button
          disabled={!data.some(d => !d.isRead)}
          onClick={() => {
            const ids = data.filter(d => !d.isRead).map(d => d.id);
            onMarkRead(ids);
            setSelected([]);
          }}
        >
          Прочитано (всё)
        </Button>

        {/* Поиск: имя / телефон */}
        <Input
          allowClear
          placeholder="Поиск: имя / телефон"
          style={{ width: 260 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          prefix={<SearchOutlined />}
        />
      </>
    }
    rightControls={
      <Popconfirm
        title="Удалить выбранные?"
        okText="Да"
        cancelText="Отмена"
        onConfirm={() => { onDelete(selected as string[]); setSelected([]); }}
      >
        <Button disabled={!anySelected} danger>Удалить выбранные</Button>
      </Popconfirm>
    }
    hint={
      <Typography.Paragraph type="secondary">
        Подсказка: потяни правую границу заголовка (двусторонняя стрелка) или <b>двойной клик</b> — авто-подбор ширины.
      </Typography.Paragraph>
    }
  >
    {data.length === 0 ? (
      <Empty description="Новых сообщений нет" />
    ) : (
      <Table<QuestionMessage>
        rowKey="id"
        size="small"
        columns={columns as ColumnsType<QuestionMessage>}
        dataSource={data}
        components={components}
        pagination={{
          ...(page != null ? { current: page } : {}),
          ...(pageSize != null ? { pageSize } : {}),
          total: total ?? 0,
          showSizeChanger: false,
          ...(onPageChange ? { onChange: onPageChange } : {}),
        }}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: (keys: React.Key[]) => setSelected(keys),
        }}
        rowClassName={(r) => (r.isRead ? '' : 'ant-table-row-selected')}
        scroll={{ x: 'max-content' }}
      />
    )}
  </NotifModalShell>
);
};
export default CustomerMessagesModal;